import { Head, Link, router, usePage } from "@inertiajs/react"
import { AlertTriangle, ArrowLeft, Building2, Users } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { CTAButton } from "@/components/collab-card/cta-button"
import { RecencyLine } from "@/components/mission-card/recency-line"
import { UrgencyChip } from "@/components/mission-card/urgency-chip"
import { BriefVideoEmpty } from "@/components/mission-detail/brief-video-empty"
import { CommunityStrip } from "@/components/mission-detail/community-strip"
import { ExpiringSoonBanner } from "@/components/mission-detail/expiring-soon-banner"
import { AddressBlock } from "@/components/mission-detail/gifting/address-block"
import {
  GiftingSection,
  isGiftingCollab,
  isPreRevealAddressCollab,
} from "@/components/mission-detail/gifting/gifting-section"
import { OfferList } from "@/components/mission-detail/gifting/offer-list"
import { ProductBlock } from "@/components/mission-detail/gifting/product-block"
import { LifecycleView } from "@/components/mission-detail/lifecycle-view"
import { MediaSlot } from "@/components/mission-detail/media-slot"
import { MissionCtaBar } from "@/components/mission-detail/mission-cta-bar"
import { RequirementsList } from "@/components/mission-detail/requirements-list"
import { DropPickerDialog } from "@/components/missions/drop-picker-dialog"
import { RequestAnotherDialog } from "@/components/missions/request-another-dialog"
import { RichTextEditor } from "@/components/rich-text-editor"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import AppLayout from "@/layouts/app-layout"
import { toAppLocale } from "@/lib/i18n/locales"
import { COVER_ASPECT_CLASS } from "@/lib/mission-cover"
import { cn } from "@/lib/utils"
import { creatorMissionsPath } from "@/routes"
import type { Locale } from "@/types"
import type { CreatorMissionDetailProps } from "@/types/mission-detail"

/**
 * Creator/MissionDetail — §08 mission detail.
 *
 * OKS-699 — rendered as a REGULAR FULL PAGE inside the creator shell
 * (`AppLayout` → `CreatorShell`), replacing the OKS-478 vaul bottom-sheet.
 * The sheet floated over an empty document, so its translucent overlay read
 * as a solid backdrop; a full page in the shell (header + bottom-tab nav
 * visible) removes the artifact. The six lifecycle views (`<LifecycleView>`)
 * and every content section are unchanged. Opened as a dedicated route
 * (`/missions/collabs/:id`); the top "← Missions" back link returns to the
 * missions list (OKS-480 wires the MissionCard tap that opens it).
 *
 * Content order (UX Spec §2): back link → full-bleed cover → urgency eyebrow →
 * brand row → title → social-proof row → written brief → brief video
 * (OKS-839: prose first, then the video as inspiration) → WHAT TO CAPTURE →
 * community strip → lifecycle CTA (last, in a sticky bar above the tab nav on
 * mobile via `<MissionCtaBar>`).
 *
 * Five states (ux-states): loading skeleton mirrors the layout (no spinner);
 * error = destructive Alert + "Try again" (Inertia partial reload); empty =
 * per-section; success = all sections; the Accept confirmation is delivered
 * as an Inertia flash toast on the missions list (UX Spec §4 + the shared
 * transitions controller redirect). The back link stays visible above every
 * state (UX Spec §3).
 *
 * error/loading are LOCAL ephemerals tied to "Try again" (this is a GET page).
 * Focus moves to the title on load (a11y, UX Spec §6); no focus trap / Esc
 * dismiss — this is a page, not a dialog.
 */
type Props = CreatorMissionDetailProps & {
  /** Test-only seams (never forwarded to the user). */
  _forceLoading?: boolean
  _forceError?: boolean
}

export default function CreatorMissionDetail(props: Props) {
  const { t, i18n } = useTranslation()
  const locale: Locale = toAppLocale(i18n.language)

  const [loading, setLoading] = useState(props._forceLoading ?? false)
  const [error, setError] = useState(props._forceError ?? false)

  // OKS-988 — the "Demander quand même" dialog (UX §S2/S3).
  //
  // ⚠️ Opens automatically when the server bounced a reason back. The refusal
  // arrives as an Inertia FIELD error on `rerequest_reason` plus the typed
  // text in `flash.rerequest_reason_draft` (302, never 303 — a 303 drops the
  // errors prop). Without the auto-open the creator lands back on the detail
  // with their words preserved in a dialog they cannot see, which reads as the
  // submit having silently done nothing.
  const page = usePage<{
    errors?: Record<string, string>
    flash?: { rerequest_reason_draft?: string }
  }>()
  const reasonError = page.props.errors?.rerequest_reason
  const reasonDraft = page.props.flash?.rerequest_reason_draft
  // OKS-990 — the drop picker, opened BY the existing primary CTA (UX §S3).
  // The sticky bar keeps exactly ONE full-width action; the picker adds no
  // button to it, so the page's hardcoded bottom padding still mirrors its
  // height.
  const [pickerOpen, setPickerOpen] = useState(false)

  // OKS-990 F-MOUNT route 2 — the reason carried from `RequestAnotherDialog`
  // into the picker, so a flagged creator's re-request is one POST. Cleared
  // whenever the picker closes, so a later normal request cannot inherit a
  // stale justification the creator never re-typed.
  const [handOffReason, setHandOffReason] = useState<string | undefined>(
    undefined,
  )
  const [requestAnotherOpen, setRequestAnotherOpen] = useState(
    Boolean(reasonError),
  )

  const headingRef = useRef<HTMLHeadingElement>(null)
  // Focus the title on open (a11y per UX Spec §6).
  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  function retry() {
    setLoading(true)
    setError(false)
    router.reload({
      only: ["collab", "mission_detail", "member_posts", "lifecycle"],
      onError: () => setError(true),
      onFinish: () => setLoading(false),
    })
  }

  const {
    collab,
    mission_detail: detail,
    member_posts: posts,
    lifecycle,
  } = props
  const deadlineDisplay = formatDeadline(detail.deadline_at, locale)

  // The lifecycle CTA renders only in the success state, in the sticky
  // `<MissionCtaBar>` above the bottom-tab nav (UX Spec §2).
  const showContent = !loading && !error

  // OKS-482 — for drop/promotional collabs in a gifting state (to_ship /
  // shipped / delivered) the product/shipping branch OWNS the body + the
  // footer CTA. The CTA is whatever `CtaResolver` affords (confirm_address /
  // preparing-disabled / confirm_receipt on `shipped` OR `delivered` /
  // shipped-disabled on a PRODUCTLESS drop / submit-picker) — we render it via
  // the shared CTAButton rather than the OKS-478 LifecycleView, which maps
  // `delivered` to a generic "Submit" that would skip confirm_receipt.
  // (OKS-992 — `shipped` was unconditionally the disabled affordance here.)
  /**
   * OKS-990 — the picker is open only while the request is still ON OFFER.
   *
   * ⚠️ DERIVED FROM PROPS, not left to `pickerOpen` alone, and that is the fix
   * for the gate's surviving red. A successful grant is a 303 that Inertia
   * resolves by re-rendering THIS SAME component with new props — the page
   * "navigates" without React unmounting anything, so `pickerOpen` survived it
   * and the dialog sat on its variant step over a collab that had already
   * moved to `to_ship`. The write was correct; only the dismissal was missing.
   *
   * Deriving it means the dialog cannot outlive the affordance under ANY route
   * to the new props — the grant, a partial reload, a browser Back — rather
   * than under the one route a success callback happens to cover. The callback
   * is still wired below, as the belt to this braces: it also resets the
   * dialog's internal step so a re-open starts clean.
   *
   * ⚠️ THE STATE, NOT THE CTA KIND, and the route-2 arm is what taught me that.
   * My first version also required `cta.kind === "request_product"`, which
   * looks tighter and is wrong: a TTL-flagged creator's primary affordance is
   * `participate_owned` (PRD A1's one tap), and they reach the picker through
   * « Demander quand même ». That clause made the picker unopenable on exactly
   * the path F-MOUNT route 2 exists to fix — a regression I wrote into this
   * file and the existing arm caught in the same run.
   */
  const requestOnOffer = lifecycle.state === "assigned"

  const gifting = isGiftingCollab(collab)
  // OKS-1002 (D4) — the pre-reveal address section. Consulted ONLY inside the
  // `!gifting` branch below, so the two are mutually exclusive structurally.
  const preRevealAddress = isPreRevealAddressCollab(collab)

  // OKS-483 — the deadline-lapse states dim the cover (UX A2/A3) and surface
  // their own copy + CTA via the LifecycleView `expired` / `reopen_requested`
  // views.
  const dimmedCover =
    collab.state === "expired" || collab.state === "reopen_requested"

  return (
    <AppLayout>
      <Head title={collab.mission.title} />
      {/* OKS-702 — clear both the sticky `<MissionCtaBar>` AND the fixed tab
          nav, whose total height grew by `env(safe-area-inset-bottom)`. Track
          the inset so the last block isn't occluded in standalone PWA;
          in-browser inset 0 → 10rem, matching the previous `pb-40`.
          OKS-739 — the `lg:pb-6` override is removed: the CTA + tab nav now
          stay fixed at all widths (iPad included), so the full bottom clearance
          must apply at `lg+` too, not shrink to `pb-6`. */}
      <div
        className="mx-auto flex w-full max-w-[480px] flex-col pb-[calc(10rem+env(safe-area-inset-bottom))]"
        data-testid="mission-detail-page"
      >
        {/* Top back affordance — visible above every state (UX Spec §3). */}
        <div className="px-4 pt-4">
          <Link
            href={creatorMissionsPath()}
            data-testid="mission-detail-back"
            className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1.5 text-sm"
          >
            <ArrowLeft aria-hidden="true" className="size-4" />
            {t("creator_mission_detail.back")}
          </Link>
        </div>

        {loading ? (
          <MissionDetailLoadingState />
        ) : error ? (
          <div className="p-4">
            <Alert
              variant="destructive"
              data-testid="mission-detail-error"
              role="alert"
            >
              <AlertTriangle aria-hidden="true" />
              <AlertTitle>{t("creator_mission_detail.error.title")}</AlertTitle>
              <AlertDescription className="mt-2">
                <Button
                  variant="outline"
                  onClick={retry}
                  data-testid="mission-detail-retry"
                >
                  {t("creator_mission_detail.error.retry")}
                </Button>
              </AlertDescription>
            </Alert>
          </div>
        ) : (
          <>
            {/* 1. Full-bleed cover — spans the column width, below the shell
                header (no sheet chrome overlaying it anymore). */}
            <MediaSlot
              hasVideo={detail.has_video}
              posterUrl={detail.poster_url}
              coverImageUrl={detail.cover_image_url}
              brandName={detail.brand_name}
              className={cn("mt-2 rounded-none", dimmedCover && "opacity-60")}
            />

            <div className="flex flex-col gap-4 p-4">
              {/* 2. Brand-less urgency eyebrow */}
              <UrgencyChip urgency={collab.mission.urgency} className="w-fit" />

              {/* 3. Brand row */}
              <div className="flex items-center gap-2">
                <Avatar className="size-8 shrink-0">
                  {collab.brand.logo_url ? (
                    <AvatarImage
                      src={collab.brand.logo_url}
                      alt={t("creator_home.brand_avatar_fallback", {
                        brand: collab.brand.name,
                      })}
                    />
                  ) : null}
                  <AvatarFallback
                    className="bg-primary text-primary-foreground text-xs font-semibold"
                    data-testid="mission-detail-avatar-fallback"
                  >
                    {brandInitials(collab.brand.name) || (
                      <Building2 className="size-4" aria-hidden="true" />
                    )}
                  </AvatarFallback>
                </Avatar>
                <span
                  className="truncate text-sm font-medium"
                  data-testid="mission-detail-brand-name"
                >
                  {collab.brand.name}
                </span>
                <Badge
                  variant="secondary"
                  className="ml-auto"
                  data-testid={`mission-detail-type-${collab.mission.type}`}
                >
                  {t(`creator_home.type.${collab.mission.type}`)}
                </Badge>
              </div>

              {/* 4. Title */}
              <h1
                ref={headingRef}
                tabIndex={-1}
                className="text-lg font-semibold focus-visible:outline-none"
                data-testid="mission-detail-title"
              >
                {collab.mission.title}
              </h1>

              {/* OKS-483 — expiring-soon nudge (renders only when the
                  server flags `expiry.tier === "expiring_soon"`; otherwise
                  null). Display-only — the Submit CTA below is unchanged. */}
              <ExpiringSoonBanner expiry={collab.expiry} />

              {/* 5. Social-proof row (brand-internal aggregate) */}
              {posts.length > 0 && (
                <p
                  className="text-muted-foreground flex items-center gap-1.5 text-sm"
                  data-testid="mission-detail-social-proof"
                >
                  <Users className="size-4" aria-hidden="true" />
                  {t("creator_mission_detail.social_proof", {
                    count: posts.length,
                  })}
                </p>
              )}

              {/* 6–8. Gifting branch (drop/promotional in a shipping state)
                  REPLACES the brief/requirements/community body — the
                  product/shipping flow is the focus of the sheet here
                  (UX Spec §Layout). Otherwise the standard detail body. */}
              {gifting ? (
                <GiftingSection
                  collab={collab}
                  productInfo={detail.product_info}
                  locale={locale}
                />
              ) : (
                <>
                  {/* OKS-1002 (D4) — PRE-REVEAL ADDRESS SECTION.

                      The creator owes an address on a drop whose product is
                      still withheld. Before this the whole gifting section —
                      address block included — was absent until `drops_at`,
                      while the sticky bar already offered the « Confirmer
                      l'adresse » POST and the SERVER ALREADY ACCEPTED IT
                      (`CreatorCollabShippingAddressesController#editable?` and
                      `ConfirmFlag`'s from-set carry no drop clause). So this
                      is not new capability — it is the missing control for a
                      write the app was already asking for and accepting.

                      ⚠️ "Exactly one of {this, GiftingSection}" is STRUCTURAL:
                      this sits inside the ELSE branch of the
                      `gifting ? … : …` ternary, whose condition IS
                      `!isGiftingCollab(collab)` — the same discipline the gift
                      preview below uses. No second boolean re-derives it.

                      ⚠️ THE PRODUCT IS NEVER NAMED HERE. No ProductBlock, no
                      gifting badge/heading/subtitle: those interpolate
                      `%{product}`, which is precisely what the reveal gate
                      withholds. The heading and the alert below name the BRAND
                      and the address only. Anything added to this block must
                      be re-checked against `gift_visible?` — a product-free
                      string carries no interpolation tripwire to catch it. */}
                  {preRevealAddress && (
                    <section
                      className="flex flex-col gap-4"
                      data-testid="mission-detail-prereveal-address"
                    >
                      <h2 className="text-base font-semibold">
                        {t(
                          collab.address_action_required
                            ? "creator_mission_detail.gifting.heading.address_pre_reveal"
                            : "creator_mission_detail.gifting.heading.preparing",
                        )}
                      </h2>

                      {/* The waiting notice, on the unconfirmed branch only —
                          on the confirmed one there is nothing to chase. Its
                          copy interpolates `brand` alone, so it is safe before
                          the reveal. Mirrors GiftingSection's treatment. */}
                      {collab.address_action_required && (
                        <Alert
                          role="status"
                          data-testid="gifting-address-waiting-notice"
                        >
                          <AlertDescription>
                            {collab.address_reminder_sent_at
                              ? t(
                                  "creator_mission_detail.gifting.address_reminded",
                                  { brand: collab.brand.name },
                                )
                              : t(
                                  "creator_mission_detail.gifting.address_waiting",
                                  { brand: collab.brand.name },
                                )}
                          </AlertDescription>
                        </Alert>
                      )}

                      <AddressBlock
                        collabId={collab.id}
                        address={collab.address}
                        accountPhone={collab.account_phone}
                        editable
                        promptMissingPhone={collab.address_action_required}
                        // OKS-1036 — same server-derived pair as GiftingSection.
                        autoConfirmed={
                          collab.address_confirmation_source ===
                            "profile_auto" &&
                          !collab.address_edited_since_confirm
                        }
                        // OKS-1037 — same server-derived value as GiftingSection.
                        confirmedByBrandName={
                          collab.address_confirmation_source === "brand"
                            ? (collab.address_confirmed_by_brand_name ?? null)
                            : null
                        }
                      />
                    </section>
                  )}

                  {/* OKS-986 — GIFT PREVIEW. The gifted product is the whole
                      incentive on a drop, so it is shown BEFORE the creator
                      accepts, above the written brief (UX D2.2).

                      ⚠️ "Exactly one of {preview, gifting section}" (AC §5.5)
                      is enforced STRUCTURALLY: this sits inside the ELSE
                      branch of the `gifting ? … : …` ternary, whose condition
                      IS `!isGiftingCollab(collab)`. There is deliberately no
                      second boolean here re-deriving that gate — a duplicate
                      could drift out of sync with the one it mirrors, and
                      "never both" would then depend on two expressions
                      agreeing rather than on one branch existing.

                      ⚠️ The heading is a NEUTRAL NOUN ("Le cadeau" / "Le
                      produit"), not a promise. This gate is not state-scoped,
                      so the preview also lands on `expired` / `declined` /
                      `completed` collabs, where "what you'll receive" would be
                      a lie. Do not "improve" this copy into something
                      persuasive without first making the gate state-aware and
                      owning that state list. */}
                  {collab.product && (
                    <section
                      className="flex flex-col gap-2"
                      data-testid="mission-detail-gift-preview"
                    >
                      <h2 className="text-base font-semibold">
                        {t(
                          collab.mission.type === "promotional"
                            ? "creator_mission_detail.gift_preview.heading_promotional"
                            : "creator_mission_detail.gift_preview.heading_drop",
                        )}
                      </h2>
                      <ProductBlock
                        product={collab.product}
                        captionFallback={detail.product_info}
                        locale={locale}
                      />

                      {/* OKS-990 (UX §S2) — the read-only offer list, below the
                          single ProductBlock and above the recency line.

                          ⚠️ ABSENT on a single-product drop, not an empty
                          container: `collab.offer` is omitted from the payload
                          entirely unless the picker applies (PRD R2), so
                          decision 5's byte-compatibility holds by construction
                          rather than by a length check here. */}
                      {collab.offer && (
                        <OfferList offer={collab.offer} locale={locale} />
                      )}

                      {/* OKS-988 (UX §S2 / UX-A2) — the recency line is the
                          LAST line of the gift section, and the secondary
                          "Demander quand même" sits immediately under it, in
                          the PAGE BODY.

                          ⚠️ Not in the sticky `MissionCtaBar`. That bar keeps
                          exactly ONE full-width action so its height stays
                          what `pb-[calc(10rem+env(safe-area-inset-bottom))]`
                          on `mission-detail-page` hardcodes; stacking a second
                          button there silently overlaps the page's last
                          content.

                          The placement is also the better one on its own
                          merits: it answers the objection at the point the
                          objection is raised. Without it, a creator who
                          finished their lipstick reads a true statement about
                          their shelf on the surface with the most context, and
                          has to navigate BACK to the list to find the way out
                          (PO ruling on §S2/A2). */}
                      <RecencyLine
                        entry={collab.entry}
                        brandName={collab.brand.name}
                        offerCount={collab.offer?.length ?? 0}
                        testId="mission-detail-recency-line"
                      />

                      {/* ⚠️ OKS-988 F2 — the helper is OUTSIDE the dual-path
                          branch. It used to be nested inside it, so a
                          single-path creator on a full mission met a disabled
                          button with no explanation. UX §S4 wants a statement,
                          not a dead control — and the creator who most needs
                          the sentence is exactly the one with no second path
                          to fall back on. */}
                      {collab.entry?.refusal && !collab.entry.dual_path && (
                        <p
                          className="text-muted-foreground text-sm"
                          data-testid="mission-detail-capacity-helper"
                        >
                          {t(
                            collab.entry.refusal === "out_of_stock"
                              ? "collabs.capacity.helper_no_stock"
                              : "collabs.capacity.helper_no_slots",
                            { brand: collab.brand.name },
                          )}
                        </p>
                      )}

                      {collab.entry?.dual_path && (
                        <div>
                          <Button
                            type="button"
                            variant="link"
                            className="text-muted-foreground h-11 px-0 underline-offset-4"
                            onClick={() => setRequestAnotherOpen(true)}
                            /* ⚠️ Disabled ONLY when the SHIPPING path is
                               refused. A stock-out must leave "Je participe
                               avec ce que j'ai" live in the bar above (PRD
                               §3.4 / AC7) — that pair, on one screen, is what
                               makes AC7 checkable at all. */
                            disabled={Boolean(collab.entry.refusal)}
                            aria-label={t(
                              "creator_home.cta.aria.request_another",
                              { title: collab.mission.title },
                            )}
                            data-testid="mission-cta-request-another"
                          >
                            {collab.entry.refusal
                              ? t(
                                  collab.entry.refusal === "out_of_stock"
                                    ? "creator_home.cta.capacity_no_stock"
                                    : "creator_home.cta.capacity_no_slots",
                                )
                              : t("creator_home.cta.request_another")}
                          </Button>
                          {collab.entry.refusal && (
                            <p
                              className="text-muted-foreground text-sm"
                              data-testid="mission-detail-capacity-helper"
                            >
                              {t(
                                collab.entry.refusal === "out_of_stock"
                                  ? "collabs.capacity.helper_no_stock"
                                  : "collabs.capacity.helper_no_slots",
                                { brand: collab.brand.name },
                              )}
                            </p>
                          )}
                        </div>
                      )}
                    </section>
                  )}

                  {/* 6. Brief — written prose FIRST, then the video-brief
                      player as inspiration (OKS-839: creators read the
                      requirements before watching the example). */}
                  <BriefSection brief={detail.brief} />

                  {/* Brief-video slot — the player (when a brief video is
                      attached) rendered BELOW the written brief. Rendered
                      whenever `attached` so a signing failure (url:null)
                      surfaces the error+Retry state rather than silently
                      vanishing (PRD §5).
                      OKS-840 — `attached:false` no longer renders NOTHING: it
                      states the absence explicitly, so "no video was provided"
                      is distinguishable from "the page failed to load". Kept
                      INSIDE the gifting ternary above, so the gifting branch
                      (which replaces this whole body) never shows it. */}
                  {detail.brief_video.attached ? (
                    <BriefVideo
                      url={detail.brief_video.url}
                      posterUrl={detail.poster_url}
                    />
                  ) : (
                    <BriefVideoEmpty />
                  )}

                  {/* 7. WHAT TO CAPTURE */}
                  <RequirementsList
                    missionDetail={detail}
                    deadlineDisplay={deadlineDisplay}
                  />

                  {/* 8. Community strip (omitted when no approved posts) */}
                  <CommunityStrip posts={posts} />
                </>
              )}
            </div>

            {/* 9. Lifecycle CTA — last in reading order, rendered in the
                sticky bar above the bottom-tab nav on mobile / inline on
                desktop (UX Spec §2). Gifting collabs in a shipping state use
                the shared CTAButton (OKS-482); everything else the
                LifecycleView. */}
            {showContent && (
              <MissionCtaBar>
                {gifting ? (
                  <CTAButton
                    cta={collab.cta}
                    missionTitle={collab.mission.title}
                    onSubmitTap={() => router.visit(creatorMissionsPath())}
                    /* ⚠️ PRESENT ONLY when the server sent an offer, i.e. when
                       R2 holds. On a single-product drop this is `undefined`,
                       the intercept branch is never reached, and the tap stays
                       one `router.post` — UX-BC clause 3. */
                    onInterceptTap={
                      collab.offer ? () => setPickerOpen(true) : undefined
                    }
                  />
                ) : (
                  <LifecycleView
                    lifecycle={lifecycle}
                    collab={collab}
                    /* ⚠️ OKS-990 F-MOUNT — THIS is the live request entry.
                       The `gifting` branch above only renders from `to_ship`
                       onward, i.e. after the grant; the request itself happens
                       at `assigned`, here. Passed only when the server sent an
                       offer, so a single-product drop keeps its one-tap POST
                       (UX-BC clause 3) and LifecycleView's `intercepts` stays
                       false. */
                    onInterceptRequest={
                      collab.offer ? () => setPickerOpen(true) : undefined
                    }
                  />
                )}
              </MissionCtaBar>
            )}

            {/* OKS-988 — mounted OUTSIDE the sticky bar and outside the gift
                section: a Dialog portals to the body, so its position in the
                tree is irrelevant to layout, and keeping it here avoids
                remounting it when the gift section re-renders (which would
                drop what the creator is typing). */}
            {/* OKS-990 (UX §S3) — mounted OUTSIDE the sticky bar and outside
                the gift section, exactly where `RequestAnotherDialog` is
                mounted and for the same reason: a Dialog portals to the body,
                so its tree position is irrelevant to layout, and keeping it
                here avoids remounting when the gift section re-renders — which
                would drop the creator's in-progress selection. */}
            {collab.offer && (
              <DropPickerDialog
                open={pickerOpen && requestOnOffer}
                onOpenChange={(next) => {
                  setPickerOpen(next)
                  if (!next) setHandOffReason(undefined)
                }}
                rerequestReason={handOffReason}
                collabId={collab.id}
                missionTitle={collab.mission.title}
                brandName={collab.brand.name}
                offer={collab.offer}
                locale={locale}
                serverErrors={{
                  mission_product_id: page.props.errors?.mission_product_id,
                  product_variant_id: page.props.errors?.product_variant_id,
                }}
              />
            )}

            {collab.entry?.dual_path && (
              <RequestAnotherDialog
                open={requestAnotherOpen}
                onOpenChange={setRequestAnotherOpen}
                collabId={collab.id}
                brandName={collab.brand.name}
                serverError={reasonError}
                offerCount={collab.offer?.length ?? 0}
                flaggedProduct={collab.entry?.recency?.product_name}
                draftReason={reasonDraft}
                /* ⚠️ Present ONLY when there is a choice to make. On a
                   single-product drop this dialog keeps posting the reason
                   directly, exactly as OKS-988 shipped it — the server has a
                   product without being told one. */
                onHandOff={
                  collab.offer
                    ? (reason) => {
                        setHandOffReason(reason)
                        setRequestAnotherOpen(false)
                        setPickerOpen(true)
                      }
                    : undefined
                }
              />
            )}
          </>
        )}
      </div>
    </AppLayout>
  )
}

/**
 * Video-brief player — the §08 video-brief variant ("Watch the brief"). A
 * standard `<video controls>` fed by a short-lived CloudFront-signed URL
 * (OKS-451; `Mission#brief_video_stream_url`), with the cover as poster.
 * Distinct from the cover media; rendered whenever a brief video is
 * ATTACHED (the caller gates on `brief_video.attached`), otherwise the brief
 * degrades to the prose/photo-text `BriefSection` (UX Spec §3).
 *
 * States (UX Spec §3, PRD §5): loading = poster + `preload="metadata"`
 * (bytes stream only on play); success = inline `aspect-video` playback;
 * error = the signed URL is unavailable (`url === null` — a SERVER-SIDE
 * signing failure, e.g. `CF_MISSION_*` not yet deployed) OR the media
 * element failed to load → inline destructive `Alert` + Retry. Retry
 * re-issues a FRESH signed URL via an Inertia partial reload of
 * `mission_detail` and remounts the `<video>`; the written brief + CTA stay
 * usable throughout. No autoplay — the creator always initiates playback.
 */
function BriefVideo({
  url,
  posterUrl,
}: {
  url: string | null
  posterUrl: string | null
}) {
  const { t } = useTranslation()
  // Media-load failure of an already-issued URL. A null `url` (server-side
  // signing failure) is a separate error trigger, folded in via `showError`.
  const [mediaFailed, setMediaFailed] = useState(false)
  // Bumped on Retry to force a fresh `<video>` element even when the
  // re-issued signed URL string is byte-identical — the browser re-attempts
  // the media load on remount.
  const [reloadKey, setReloadKey] = useState(0)

  const showError = url === null || mediaFailed

  function retry() {
    setMediaFailed(false)
    setReloadKey((k) => k + 1)
    // Re-run `CreatorMissionDetailQuery` server-side → fresh signed URL in
    // the `mission_detail` prop, without a full page reload (UX Spec §4).
    // Once the CloudFront deploy (OKS-726) lands, this resolves to a URL.
    router.reload({ only: ["mission_detail"] })
  }

  return (
    <section
      data-testid="mission-detail-brief-video"
      className="flex flex-col gap-2"
    >
      <h2 className="text-sm font-semibold">
        {t("creator_mission_detail.brief_video.title")}
      </h2>
      {showError ? (
        <Alert
          variant="destructive"
          data-testid="mission-detail-brief-video-error"
          role="alert"
        >
          <AlertTriangle aria-hidden="true" />
          <AlertTitle>
            {t("creator_mission_detail.brief_video.error")}
          </AlertTitle>
          <AlertDescription className="mt-2">
            <Button
              variant="outline"
              onClick={retry}
              data-testid="mission-detail-brief-video-retry"
            >
              {t("creator_mission_detail.brief_video.retry")}
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        /* Brand-authored explainer; no caption track is available, and the
           prose brief below carries the equivalent text content. */
        <video
          key={reloadKey}
          src={url ?? undefined}
          poster={posterUrl ?? undefined}
          controls
          preload="metadata"
          onError={() => setMediaFailed(true)}
          className="aspect-video w-full rounded-xl bg-black"
        />
      )}
    </section>
  )
}

/**
 * Brief section — renders the read-only TipTap brief, with a "Read more"
 * expander when the content overflows ~6 lines. Empty brief → muted line.
 */
function BriefSection({
  brief,
}: {
  brief: CreatorMissionDetailProps["mission_detail"]["brief"]
}) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const [overflows, setOverflows] = useState(false)
  const bodyRef = useRef<HTMLDivElement>(null)

  const empty = isBriefEmpty(brief)

  useEffect(() => {
    if (empty) return
    const el = bodyRef.current
    if (!el) return
    setOverflows(el.scrollHeight > el.clientHeight + 4)
  }, [empty])

  return (
    <section data-testid="mission-detail-brief" className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold">
        {t("creator_mission_detail.brief.title")}
      </h2>
      {empty ? (
        <p
          className="text-muted-foreground text-sm"
          data-testid="mission-detail-brief-empty"
        >
          {t("creator_mission_detail.brief.empty")}
        </p>
      ) : (
        <>
          <div
            ref={bodyRef}
            className={cn("text-sm", !expanded && "max-h-40 overflow-hidden")}
            data-testid="mission-detail-brief-body"
          >
            <RichTextEditor
              name="mission-detail-brief"
              defaultValue={brief}
              editable={false}
              testId="mission-detail-brief-editor"
            />
          </div>
          {overflows && (
            <Button
              type="button"
              variant="link"
              onClick={() => setExpanded((v) => !v)}
              data-testid="mission-detail-brief-toggle"
              className="h-auto self-start p-0 text-sm"
            >
              {expanded
                ? t("creator_mission_detail.brief.read_less")
                : t("creator_mission_detail.brief.read_more")}
            </Button>
          )}
        </>
      )}
    </section>
  )
}

/** Skeleton mirroring the populated layout — no spinners (UX Spec §3). */
function MissionDetailLoadingState() {
  return (
    <div
      className="flex flex-col"
      data-testid="mission-detail-loading"
      aria-busy="true"
      aria-live="polite"
    >
      <Skeleton className={cn(COVER_ASPECT_CLASS, "w-full rounded-none")} />
      <div className="flex flex-col gap-4 p-4">
        <Skeleton className="h-5 w-24 rounded-full" />
        <div className="flex items-center gap-2">
          <Skeleton className="size-8 rounded-full" />
          <Skeleton className="h-3 w-24" />
        </div>
        <Skeleton className="h-6 w-3/4" />
        {/* OKS-986 — gift-preview block (heading + the 60px `ProductBlock`
            row), sized to the real thing so a gifting mission's detail does
            not shift when the props land. */}
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-[84px] w-full rounded-xl" />
        {/* OKS-988 — the recency line and the secondary "Demander quand même"
            sit under the gift block on a flagged collab (UX §S2), so the
            skeleton reserves both: one text line + one 44px action row.

            ⚠️ Honest limitation, stated rather than implied: the skeleton
            cannot know which shape will resolve. Sized to the FLAGGED shape
            because that is the one UX §3 names and the one whose growth was
            previously unreserved; an unflagged collab therefore resolves
            slightly SHORTER than its skeleton. That trade is deliberate —
            content settling upward is less disruptive than the primary action
            being pushed down under the reader's thumb after they have already
            reached for it. */}
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-11 w-48 rounded-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-36" />
        </div>
        <Skeleton className="h-11 w-full rounded-full" />
      </div>
    </div>
  )
}

/** A brief is empty when there's no renderable content node. */
function isBriefEmpty(
  brief: CreatorMissionDetailProps["mission_detail"]["brief"],
): boolean {
  if (!brief || typeof brief !== "object") return true
  const content = (brief as { content?: unknown[] }).content
  return !Array.isArray(content) || content.length === 0
}

/** 1-2 uppercase brand initials; empty input → "" (caller shows Building2). */
function brandInitials(name: string): string {
  const tokens = name.trim().split(/\s+/).filter(Boolean).slice(0, 2)
  return tokens.map((tok) => tok[0]?.toUpperCase() ?? "").join("")
}

/** Locale-aware short date; null/invalid → null. */
function formatDeadline(
  iso: string | null,
  locale: "en" | "fr",
): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString(locale === "fr" ? "fr-FR" : "en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
}
